import {
  computed,
  effect,
  inject,
  Injectable,
  signal,
  untracked,
  WritableSignal,
} from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";
import { StateService } from "@bitwarden/web-vault/app/core";
import {
  GLOSS_CREDITS_SYMBOL,
  GLOSS_PLATINUM_CREDITS_SYMBOL,
} from "@bitwarden/web-vault/app/models/constants/global.constants";
import {
  LAST_PURCHASE_CHECK_KEY,
  PURCHASE_ID_KEY,
  PURCHASE_ID_MAX_AGE_MS,
  PURCHASE_ID_PACK_KEY,
  PURCHASE_ID_TS_KEY,
  STRIPE_RETURN_PURCHASE_ID_PARAM,
  UNRESOLVED_CONVERSIONS_KEY,
} from "@bitwarden/web-vault/app/models/constants/credits.constants";
import { CreditConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { ForexRateView } from "@bitwarden/web-vault/app/models/view/reference-data/forex-rate.view";
import { pollUntil } from "@bitwarden/web-vault/app/shared/utils/poll-until";
import { capFetch } from "@bitwarden/web-vault/utils/fetch";
import {
  ClientTransactionSource,
  IConversionLeg,
  IConversionResponse,
  ICreditAccount,
  ITransaction as CreditTransaction,
  ITransactionResponse,
} from "@bitwarden/web-vault/app/models/interfaces/credits.interfaces";
import { TransactionType } from "@bitwarden/web-vault/app/models/enum/credits.enum";
import {
  PluginConfigService,
  PluginIdentifier,
} from "@bitwarden/web-vault/app/services/store/data-plugin/plugin-config.service";
import { TransactionStoreService } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.service";
import { GlossCreditsApi } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits.api";
import { GlossCreditsAccountService } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-account.service";
import { mapGlossCreditsTransactionToStoreModel } from "@bitwarden/web-vault/app/services/syncing/gloss-credits/gloss-credits-transaction.mapper";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { NotificationEnum } from "@bitwarden/web-vault/app/models/enum/notification.enum";
import { sequentialize } from "@bitwarden/common/misc/sequentialize";

// webpack DefinePlugin replaces process.env.CREDIT with the literal object at build time.
// In Jest (Node.js) it stays as a JSON string, so we parse it when needed.
const _rawCreditEnv = process.env.CREDIT;
const CREDIT_CONFIG = (
  typeof _rawCreditEnv === "string" ? JSON.parse(_rawCreditEnv) : _rawCreditEnv
) as CreditConfigType;

/** How long a conversion with an unknown outcome is offered for retry under its old id (see
 *  CreditsService.unresolvedConversion). A retry after a 5xx or a closed drawer happens within
 *  minutes; a same-amount conversion after this is treated as a new one. */
export const UNRESOLVED_CONVERSION_TTL_MS = 15 * 60_000;

/** A conversion sent whose outcome isn't known yet; see CreditsService.unresolvedConversions. */
interface UnresolvedConversion {
  accountId: string;
  amount: number;
  conversionId: string;
  sentAt: number;
}

function isUnresolvedConversion(value: unknown): value is UnresolvedConversion {
  const c = value as UnresolvedConversion | null;
  return (
    typeof c?.accountId === "string" &&
    typeof c.conversionId === "string" &&
    Number.isFinite(c.amount) &&
    Number.isFinite(c.sentAt)
  );
}

/** A conversion found committed after an unknown outcome (CreditsService.resolveConversion): its
 *  legs as the fetched transactions recorded them, either of which may be missing from the fetch. */
export type ResolvedConversion = Pick<IConversionResponse, "conversionId"> & {
  withdrawal?: ResolvedConversionLeg;
  deposit?: ResolvedConversionLeg;
};

/** balanceAfter is missing when neither the leg nor the refreshed account has it. */
type ResolvedConversionLeg = Omit<IConversionLeg, "balanceAfter"> & { balanceAfter?: number };

/** Past the TTL - or "sent" more than a TTL in the future, after the device clock was turned back:
 *  now that records outlive a session (#6247), such a record would otherwise never expire. */
const isExpired = (c: UnresolvedConversion, now: number): boolean =>
  Math.abs(now - c.sentAt) > UNRESOLVED_CONVERSION_TTL_MS;

export interface CreditPack {
  productId: string;
  label: string;
  credits: number;
  price: number;
  currency: string;
}

/**
 * Thrown by pollPurchaseStatus when the backend reports the purchase definitively ended
 * ("failed"/"cancelled"), as opposed to a timeout or network error where the outcome is still
 * unknown. Callers use it to show "purchase failed" instead of "payment received, processing".
 */
export class PurchaseEndedError extends Error {
  constructor(readonly status: string) {
    super(`Purchase ended with status: ${status}`);
    this.name = "PurchaseEndedError";
  }
}

export interface PurchaseDetail {
  status: "pending" | "completed" | "failed";
  credits?: number;
  newBalance?: number;
  failureReason?: string;
}

/** A Stripe return finalizePendingStripeReturn finalized: which purchase, and its outcome. */
export interface FinalizedStripeReturn {
  purchaseId: string;
  detail: PurchaseDetail;
}

export interface CreditPurchaseRecord {
  purchaseId: string;
  status: "pending" | "completed" | "failed";
  productId?: string;
  credits?: number;
  newBalance?: number;
  createdAt?: string;
}

/** Thrown by convertCredits() on a non-2xx response; carries the HTTP status so callers can
 *  distinguish retryable (500) from non-retryable (400/401/404) failures. `serverMessage` is the
 *  unprefixed message suitable for display to the user; `message` keeps the dev-oriented prefix
 *  for logs/stack traces. */
export class ConversionError extends Error {
  readonly status: number;
  readonly serverMessage: string;

  constructor(message: string, status: number, serverMessage: string) {
    super(message);
    this.name = "ConversionError";
    this.status = status;
    this.serverMessage = serverMessage;
  }

  /** True when the conversion may still have committed (a 5xx/gateway status, or no real HTTP
   *  status at all), false for a definitive rejection. The single source for this split, used by
   *  both convertCredits' unresolved-conversion bookkeeping and the drawer's error handling. */
  get outcomeUnknown(): boolean {
    return isOutcomeUnknownStatus(this.status);
  }
}

function isOutcomeUnknownStatus(status: number): boolean {
  return status < 200 || status >= 500;
}

@Injectable({
  providedIn: "root",
})
export class CreditsService {
  readonly pluginName = PluginIdentifier.CREDIT;
  readonly baseUrl = inject(GlossCreditsApi).baseUrl;
  readonly apiStage = CREDIT_CONFIG.apiStage;

  private log: LogService = inject(LogService);
  private legacyAppState: StateService = inject(StateService);
  private pluginConfigService: PluginConfigService = inject(PluginConfigService);
  private transactionStoreService: TransactionStoreService = inject(TransactionStoreService);
  private glossCreditsApi: GlossCreditsApi = inject(GlossCreditsApi);
  private glossCreditsAccountService: GlossCreditsAccountService = inject(
    GlossCreditsAccountService,
  );
  private notifyService: ConfirmationDialogService = inject(ConfirmationDialogService);

  private _transactions = signal<CreditTransaction[]>([]);
  readonly transactions = this._transactions.asReadonly();
  private readonly _accounts = signal<ICreditAccount[]>([]);
  readonly accounts = this._accounts.asReadonly();
  private _enabled = signal<boolean>(false);
  readonly enabled = this._enabled.asReadonly();
  readonly config = signal(this.pluginConfigService.getConfig(this.pluginName));
  private readonly _userId = signal<string | null>(null);
  readonly userId = this._userId.asReadonly();
  private readonly _userName = signal<string | null>(null);
  readonly userName = this._userName.asReadonly();

  glossBankingApiKey = computed(() => this.config()?.apiKey);
  readonly totalGoldCredits = computed(() =>
    this.calculateTotalCredits(this._accounts(), GLOSS_CREDITS_SYMBOL),
  );
  readonly totalPlatinumCredits = computed(() =>
    this.calculateTotalCredits(this._accounts(), GLOSS_PLATINUM_CREDITS_SYMBOL),
  );
  readonly rewardTransactions = computed(() => {
    const firstId = this._accounts()[0]?._id;
    if (!firstId) {
      return [];
    }
    return this.transactions().filter((t) => t.accountId === firstId);
  });
  readonly creditTransactions = this.rewardTransactions;

  creditAmountToRedeem: WritableSignal<number> = signal(0);
  private _initialized = false;

  /** Conversions sent whose outcome isn't known yet, by conversionId, oldest first: in flight, or
   *  failed with a 5xx or a network error, after which they may still have committed. Kept here
   *  rather than in the drawer, which the user can close mid-request or after the error, so a
   *  reopened drawer resubmits the same conversionId (see conversionIdFor) instead of converting
   *  twice. One per conversion, so a newer conversion sent while an older one is in flight can't
   *  drop the older one's record. Each is removed once its response gives a definitive outcome,
   *  and ignored after UNRESOLVED_CONVERSION_TTL_MS: past that, a conversion of the same amount is
   *  a new one, not a retry, and must not replay the old one.
   *
   *  Mirrored in localStorage (UNRESOLVED_CONVERSIONS_KEY) and read back on first use, so a reload
   *  or an app kill mid-request doesn't lose them (#6247) - most likely on mobile, where a hung
   *  request gets force-quit. Records stay scoped to their account: logout doesn't reload, and a
   *  device can be shared.
   *
   *  Read afresh on every use, and every change is made to that fresh read and written straight
   *  back: another tab (web) shares the storage, and writing back a copy read earlier would drop
   *  the records it added since. Once storage can't be written, this instance keeps its own copy
   *  for the rest of the session and stops writing: a later write of that copy would erase what
   *  other tabs stored meanwhile. */
  private get unresolvedConversions(): Map<string, UnresolvedConversion> {
    if (!this.unresolvedConversionsMemoryOnly) {
      this.unresolvedConversionsCache = this.readUnresolvedConversions();
    }
    return (this.unresolvedConversionsCache ??= new Map());
  }
  private unresolvedConversionsCache?: Map<string, UnresolvedConversion>;
  private unresolvedConversionsMemoryOnly = false;

  /** conversionIds with a convertCredits request, or the resolveConversion check after it, in
   *  flight. Shared by every drawer: one closed mid-request and reopened offers the same id, and a
   *  second request with it went out while the first was pending, ending in two receipts for one
   *  conversion (#6248). */
  private readonly inFlightConversionIds = signal<ReadonlySet<string>>(new Set());
  /** conversionIds known to have committed this session: by their own 2xx (a replay's included)
   *  or found by resolveConversion. With the fetched transactions' ids, see isConversionCommitted. */
  private readonly committedConversionIds = signal<ReadonlySet<string>>(new Set());

  /** Whether a request with this conversionId, or the check of its outcome, is in flight (a
   *  signal read: reactive). */
  isConversionInFlight(conversionId: string): boolean {
    return this.inFlightConversionIds().has(conversionId);
  }

  /** Whether this conversion is known to have committed, so its id must not be offered again:
   *  submitting it would only replay it (a signal read: reactive). */
  isConversionCommitted(conversionId: string): boolean {
    return (
      this.committedConversionIds().has(conversionId) ||
      this.transactions().some((t) => t.conversionId === conversionId)
    );
  }

  private markConversion(
    ids: typeof this.inFlightConversionIds,
    conversionId: string,
    present: boolean,
  ): void {
    ids.update((current) => {
      const next = new Set(current);
      if (present) {
        next.add(conversionId);
      } else {
        next.delete(conversionId);
      }
      return next;
    });
  }

  /** The stored records still within the TTL, in the order they were stored (oldest first; not
   *  by sentAt, which a clock turned back would reorder); drops the rest from storage. */
  private readUnresolvedConversions(): Map<string, UnresolvedConversion> {
    let stored: unknown;
    try {
      stored = JSON.parse(localStorage.getItem(UNRESOLVED_CONVERSIONS_KEY) ?? "[]");
    } catch {
      // Unreadable: start empty, and the write below clears it.
      stored = null;
    }
    const now = Date.now();
    const all = Array.isArray(stored) ? stored : [];
    const live = all.filter(isUnresolvedConversion).filter((c) => !isExpired(c, now));
    const records = new Map(live.map((c) => [c.conversionId, c]));
    if (live.length !== all.length || !Array.isArray(stored)) {
      this.writeUnresolvedConversions(records);
    }
    return records;
  }

  private writeUnresolvedConversions(records: Map<string, UnresolvedConversion>): void {
    if (this.unresolvedConversionsMemoryOnly) {
      return;
    }
    try {
      if (records.size) {
        localStorage.setItem(UNRESOLVED_CONVERSIONS_KEY, JSON.stringify([...records.values()]));
      } else {
        localStorage.removeItem(UNRESOLVED_CONVERSIONS_KEY);
      }
    } catch (error) {
      // Storage full or unavailable: the in-memory records still guard this session.
      this.unresolvedConversionsMemoryOnly = true;
      this.unresolvedConversionsCache = records;
      this.log.warning(`Couldn't persist unresolved conversions: ${String(error)}`);
    }
  }

  constructor() {
    effect(() => {
      const config = this.pluginConfigService.getConfig(this.pluginName);
      if (config?.enabled && !this._initialized) {
        this._initialized = true;
        untracked(() => {
          this.config.set(config);
          (async () => {
            await this.fetch();
          })().catch((e) => this.log.error(`Credits init error: ${e?.message}`));
        });
      }
    });
  }

  syncConfigFromVault(): void {
    this.config.set(this.pluginConfigService.getConfig(this.pluginName));
  }

  async authenticate(): Promise<void> {
    const res = await this.glossCreditsApi.auth();
    this._userId.set(res.user.id);
    this._userName.set(res.user.username);
    await this.transactionStoreService.saveGlossCreditsReferences(
      res.user.references.map((ref) => new ForexRateView(ref)),
    );
  }

  /** Ensure gloss-credit Mongo user has dynamoId = gloss-api user id (required for auto-redeem). */
  async ensureGlossApiUserLinked(): Promise<void> {
    const dynamoId = await this.legacyAppState.getUserId();
    if (!dynamoId?.trim()) {
      return;
    }

    try {
      await this.glossCreditsApi.linkGlossUser(dynamoId.trim());
    } catch (error) {
      this.log.warning(
        `Gloss Credits gloss-api link skipped: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }

  async saveGlossCreditsReferences(): Promise<void> {
    const res = await this.glossCreditsApi.auth();
    await this.transactionStoreService.saveGlossCreditsReferences(
      res.user.references.map((reference) => new ForexRateView(reference)),
    );
  }

  async fetch(params: unknown = {}): Promise<CreditTransaction[]> {
    this.syncConfigFromVault();
    const config = this.config();

    if (!config || config.enabled !== true) {
      return [];
    }

    this._enabled.set(config.enabled);
    await this.authenticate();
    await this.ensureGlossApiUserLinked();
    const accountList = await this.fetchUserAccounts();
    this._accounts.set(accountList);
    await this.fetchCredits();
    return this.transactions();
  }

  /**
   * @param source - What produced it, for the one source the backend takes from a client
   *   ("ad_reward": an ad watch it has no SSV callback for). Anything else is written "manual".
   */
  async createTransaction(
    accountId: string,
    type: TransactionType,
    currency: string,
    amount: number,
    auto: boolean = true,
    source?: ClientTransactionSource,
  ): Promise<CreditTransaction> {
    if (!accountId || !accountId.trim()) {
      throw new Error("Account ID is required");
    }
    if (!["deposit", "withdrawal"].includes(type)) {
      throw new Error("Type must be either 'deposit' or 'withdrawal'");
    }
    if (!currency || !currency.trim()) {
      throw new Error("Currency is required");
    }
    if (amount < 0.01) {
      throw new Error("Amount must be at least 0.01");
    }

    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }

    const response = await capFetch<
      { message?: string; transaction?: CreditTransaction } & CreditTransaction
    >({
      url: `${this.baseUrl}${CREDIT_CONFIG.endpoint.transactions}`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      data: {
        accountId,
        type,
        currency,
        amount,
        auto,
        ...(source ? { source } : {}),
      },
    });

    const data = this.assertOk(response, "Failed to create transaction") as {
      transaction?: CreditTransaction;
    } & Partial<CreditTransaction>;
    const txData = (data.transaction ?? data) as unknown as ITransactionResponse;
    await this.saveNewTransactionToVault(txData);
    try {
      await this.fetch();
    } catch (refreshError) {
      // assertOk above already confirmed the deposit/withdrawal succeeded server-side; only the
      // local balance refresh failed. Don't let that masquerade as a failed transaction - a
      // caller like createTransactionOnTaskComplete() would otherwise see this throw, collapse
      // it into the same `false` it returns for "no account to deposit into", and treat a
      // reward that's already banked as not applied.
      this.log.warning(`Post-transaction balance refresh failed: ${String(refreshError)}`);
      this.notifyService.notify(
        NotificationEnum.info,
        "balanceUpdatePending",
        "creditsTransactionRefreshFailed",
      );
    }
    if (data.transaction) {
      return data.transaction;
    }
    return data as CreditTransaction;
  }

  /** The amount of this account's newest conversion whose outcome is unknown, or null if there's
   *  none. Its id is conversionIdFor(accountId, amount). */
  unresolvedConversion(accountId: string): number | null {
    return this.liveUnresolvedConversions(accountId)[0]?.amount ?? null;
  }

  /** The conversionId to submit `amount` under: an unresolved conversion's if one has the same
   *  account and amount, so a retry can only ever commit once; otherwise a fresh one. */
  conversionIdFor(accountId: string, amount: number): string {
    const unresolved = this.liveUnresolvedConversions(accountId).find((c) => c.amount === amount);
    return unresolved?.conversionId ?? crypto.randomUUID();
  }

  /** This account's unresolved conversions still within the TTL, newest first. Expired ones (any
   *  account's), and ones the fetched transactions show committed (#6248), are dropped from
   *  storage on the way: offering a committed one's id would only replay it. */
  private liveUnresolvedConversions(accountId: string) {
    const now = Date.now();
    const records = this.unresolvedConversions;
    let dropped = false;
    for (const [id, c] of Array.from(records)) {
      if (isExpired(c, now) || this.isConversionCommitted(id)) {
        records.delete(id);
        dropped = true;
      }
    }
    if (dropped) {
      this.writeUnresolvedConversions(records);
    }
    return Array.from(records.values())
      .filter((c) => c.accountId === accountId)
      .reverse();
  }

  /**
   * After convertCredits failed with an unknown outcome (5xx, network error): refreshes and looks
   * for the conversion among the fetched transactions, where the backend records its conversionId
   * on both legs (#6248). If it committed, its unresolved record is dropped and the outcome is
   * returned in convertCredits' shape (a leg missing from the fetch is left out; balanceAfter is
   * the leg's own, as convertCredits' response has it, else the refreshed total). Null if it isn't
   * there - not committed, or not known yet - or the
   * refresh failed: the record stays, so a retry still reuses its id.
   */
  async resolveConversion(conversionId: string): Promise<ResolvedConversion | null> {
    // Still in flight while the outcome is checked: a drawer reopened meanwhile offers this id,
    // and a Convert then would send it again (#6248).
    this.markConversion(this.inFlightConversionIds, conversionId, true);
    try {
      return await this.findCommittedConversion(conversionId);
    } finally {
      this.markConversion(this.inFlightConversionIds, conversionId, false);
    }
  }

  private async findCommittedConversion(conversionId: string): Promise<ResolvedConversion | null> {
    try {
      await this.fetch();
    } catch (error) {
      this.log.warning(`Couldn't refresh to resolve conversion ${conversionId}: ${String(error)}`);
      return null;
    }
    const legs = this.transactions().filter((t) => t.conversionId === conversionId);
    if (!legs.length) {
      return null;
    }
    const records = this.unresolvedConversions;
    if (records.delete(conversionId)) {
      this.writeUnresolvedConversions(records);
    }
    this.markConversion(this.committedConversionIds, conversionId, true);
    // The backend stores both legs' amounts as positive; abs() in case that ever changes. Without
    // a balanceAfter, the converted account's refreshed balance (not every account's total).
    const leg = (currency: string) => {
      const t = legs.find((l) => l.currency === currency);
      if (!t) {
        return undefined;
      }
      // An account missing from the refresh (its request failed) leaves it out, rather than
      // reporting a 0 balance: the receipt then falls back and logs the gap.
      const account = this._accounts().filter((a) => a._id === t.accountId);
      const balanceAfter = Number.isFinite(t.balanceAfter)
        ? t.balanceAfter
        : account.length
          ? this.calculateTotalCredits(account, currency)
          : undefined;
      return { currency, amount: Math.abs(t.amount), balanceAfter };
    };
    return {
      conversionId,
      withdrawal: leg(GLOSS_PLATINUM_CREDITS_SYMBOL),
      deposit: leg(GLOSS_CREDITS_SYMBOL),
    };
  }

  async convertCredits({
    accountId,
    amount,
    conversionId,
  }: {
    accountId: string;
    amount: number;
    conversionId: string;
  }): Promise<IConversionResponse> {
    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }

    // Set before the request: if it throws (network error), the outcome stays unknown. Deleted
    // first so a resend moves to the end (newest). Expired ones are dropped when read.
    const records = this.unresolvedConversions;
    records.delete(conversionId);
    records.set(conversionId, { accountId, amount, conversionId, sentAt: Date.now() });
    this.writeUnresolvedConversions(records);
    this.markConversion(this.inFlightConversionIds, conversionId, true);
    const response = await capFetch<IConversionResponse & { message?: string; errors?: unknown }>({
      url: `${this.baseUrl}${CREDIT_CONFIG.endpoint.convert}`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      data: {
        accountId,
        amount,
        fromCurrency: GLOSS_PLATINUM_CREDITS_SYMBOL,
        toCurrency: GLOSS_CREDITS_SYMBOL,
        conversionId,
      },
    }).finally(() => this.markConversion(this.inFlightConversionIds, conversionId, false));

    // A 2xx (committed) or a 4xx (rejected) is definitive; a 5xx may have committed. Only clears
    // this conversion's own record, not a newer one sent while it was in flight.
    if (!isOutcomeUnknownStatus(response.status)) {
      const records = this.unresolvedConversions;
      records.delete(conversionId);
      this.writeUnresolvedConversions(records);
    }
    if (response.status >= 200 && response.status <= 299) {
      this.markConversion(this.committedConversionIds, conversionId, true);
    }
    if (response.status < 200 || response.status > 299) {
      const data = response.data as { message?: string; errors?: unknown } | null;
      const msg = data?.message
        ? String(data.message)
        : data?.errors
          ? JSON.stringify(data.errors)
          : `HTTP ${response.status}`;
      throw new ConversionError(`Failed to convert credits: ${msg}`, response.status, msg);
    }

    try {
      await this.fetch();
    } catch (refreshError) {
      // Conversion already succeeded server-side; only the local balance refresh failed.
      // Don't let this masquerade as a conversion failure — but the displayed Platinum/Gold
      // balances are now stale, so tell the user rather than letting them assume it just worked.
      this.log.warning(`Post-conversion balance refresh failed: ${String(refreshError)}`);
      this.notifyService.notify(
        NotificationEnum.info,
        "balanceUpdatePending",
        "creditsConvertedRefreshFailed",
      );
    }
    return response.data;
  }

  async listPacks(): Promise<CreditPack[]> {
    // GET /api/credits/packs — no auth
    const response = await capFetch<CreditPack[]>({
      url: `${this.baseUrl}${CREDIT_CONFIG.endpoint.creditPacks}`,
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });
    const data = this.assertOk(response, "Failed to load credit packs");
    return Array.isArray(data) ? data : [];
  }

  async createStripeCreditCheckout(
    productId: string,
  ): Promise<{ purchaseId: string; url: string }> {
    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }
    const response = await this.postCheckoutRequest<{
      purchaseId?: string;
      url?: string;
      message?: string;
    }>(CREDIT_CONFIG.endpoint.creditPurchaseCheckoutStripe, apiKey, productId);
    const data = this.assertOk(response, "Failed to create checkout session");
    if (!data.purchaseId || !data.url) {
      throw new Error("Failed to create checkout session: missing purchaseId or url in response");
    }
    return { purchaseId: data.purchaseId, url: data.url };
  }

  async createAppleCreditCheckout(
    productId: string,
  ): Promise<{ purchaseId: string; appAccountToken: string }> {
    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }
    const response = await this.postCheckoutRequest<{
      purchaseId?: string;
      appAccountToken?: string;
      message?: string;
    }>(CREDIT_CONFIG.endpoint.creditPurchaseCheckoutApple, apiKey, productId);
    const data = this.assertOk(response, "Failed to create Apple checkout");
    if (!data.purchaseId || !data.appAccountToken) {
      throw new Error(
        "Failed to create Apple checkout: missing purchaseId or appAccountToken in response",
      );
    }
    return { purchaseId: data.purchaseId, appAccountToken: data.appAccountToken };
  }

  async pollPurchaseStatus(purchaseId: string): Promise<void> {
    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }
    const url = `${this.baseUrl}${CREDIT_CONFIG.endpoint.creditPurchaseStatus}`.replace(
      ":purchaseId",
      encodeURIComponent(purchaseId),
    );

    const result = await pollUntil({
      poll: async () => {
        const response = await capFetch<{ status: string }>({
          url,
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
        });
        const pollData = this.assertOk(response, "Poll failed");
        return pollData?.status;
      },
      isReady: (status) => status === "ready",
      isFailed: (status) => status === "failed" || status === "cancelled",
      getFailureError: (status) => new PurchaseEndedError(status),
      intervalMs: 2000,
      backoffMultiplier: 1.5,
      maxIntervalMs: 15000,
      deadlineMs: 90_000,
    });

    if (!result.ready) {
      throw new Error("Purchase status polling timed out");
    }
  }

  async getPurchaseDetail(purchaseId: string): Promise<PurchaseDetail> {
    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }
    const url = `${this.baseUrl}${CREDIT_CONFIG.endpoint.creditPurchaseDetail}`.replace(
      ":purchaseId",
      encodeURIComponent(purchaseId),
    );
    const response = await capFetch<PurchaseDetail & { message?: string }>({
      url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
    });
    return this.assertOk(response, "Failed to get purchase detail") as PurchaseDetail;
  }

  async waitForFinalDetail(purchaseId: string): Promise<PurchaseDetail> {
    const result = await pollUntil({
      poll: () => this.getPurchaseDetail(purchaseId),
      isReady: (detail) => detail.status === "completed" || detail.status === "failed",
      intervalMs: 1000,
      backoffMultiplier: 1.5,
      maxIntervalMs: 5000,
      deadlineMs: 15_000,
    });
    return result.value;
  }

  async reactToPurchaseDetail(detail: PurchaseDetail): Promise<void> {
    if (detail.status === "completed") {
      try {
        await this.fetch();
      } catch {
        this.log.warning("Failed to refresh balance after successful purchase");
      }
      this.notifyService.notify(NotificationEnum.success, "succeeded", "creditsPurchased");
      this.bumpLastPurchaseCheck();
      return;
    }
    if (detail.status === "failed") {
      this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
      return;
    }
    // Still pending after the poll deadline — not a confirmed failure. Login-recovery
    // (checkAndReactToPurchaseHistory) will pick it up once the backend finalizes it.
    this.notifyService.notify(NotificationEnum.info, "processing", "creditsPurchaseProcessing");
  }

  /**
   * Polls a purchase to its outcome and toasts it (see reactToPurchaseDetail). Never rejects:
   * - status "failed": the status poll reported the purchase failed or was cancelled.
   * - status "pending": the outcome couldn't be confirmed, either because the backend was still
   *   pending at the poll deadline or because polling threw (timeout, network, ...). The payment
   *   may already have been charged, so callers must not treat this as a failure and invite a
   *   second payment; login-recovery reconciles it later.
   */
  async finalizePurchase(purchaseId: string): Promise<PurchaseDetail> {
    let detail: PurchaseDetail;
    try {
      await this.pollPurchaseStatus(purchaseId);
      detail = await this.waitForFinalDetail(purchaseId);
    } catch (error) {
      if (!(error instanceof PurchaseEndedError)) {
        // Mapped to "pending" below, which looks exactly like a slow webhook to the user. Log it so
        // a config fault (e.g. "Credit API key is not available", thrown before any poll) shows up.
        this.log.warning(`Purchase ${purchaseId} unconfirmed: ${error}`);
      }
      detail =
        error instanceof PurchaseEndedError
          ? { status: "failed", failureReason: error.status }
          : { status: "pending" };
    }
    await this.reactToPurchaseDetail(detail);
    return detail;
  }

  /**
   * Completes a Stripe checkout that was started before redirecting away from the app.
   * Idempotent — safe to call from bootstrap and from the credits page on the same return.
   *
   * Returns the purchase id it finalized and its final PurchaseDetail (see finalizePurchase), so a
   * caller on the credits page can show a receipt for that purchase — bootstrap callers ignore the
   * return value and rely on reactToPurchaseDetail's toast as before. null when nothing was pending
   * to finalize.
   */
  @sequentialize(() => "stripeFinalize")
  async finalizePendingStripeReturn(): Promise<FinalizedStripeReturn | null> {
    this.capturePurchaseIdFromReturnUrl();

    const pending = this.readFreshPendingPurchase();
    if (!pending) {
      // Also drops a stale purchase id, so the credits page doesn't keep treating it as a pending
      // return (flashing the processing panel) until bootstrap gets round to clearing it.
      this.clearPendingPurchaseKeys();
      this.clearStripeReturnFromUrl();
      return null;
    }

    try {
      const detail = await this.finalizePurchase(pending.purchaseId);
      return { purchaseId: pending.purchaseId, detail };
    } finally {
      this.clearPendingPurchaseKeys();
      this.clearStripeReturnFromUrl();
    }
  }

  async getPurchaseHistory(since?: string): Promise<CreditPurchaseRecord[]> {
    const apiKey = this.glossBankingApiKey();
    if (!apiKey) {
      throw new Error("Credit API key is not available");
    }
    const base = `${this.baseUrl}${CREDIT_CONFIG.endpoint.creditPurchaseHistory}`;
    const url = since ? `${base}?since=${encodeURIComponent(since)}` : base;
    const response = await capFetch<
      CreditPurchaseRecord[] | { purchases?: CreditPurchaseRecord[] }
    >({
      url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
    });
    const data = this.assertOk(response, "Failed to fetch purchase history");
    if (Array.isArray(data)) {
      return data;
    }
    if (data && "purchases" in data && Array.isArray(data.purchases)) {
      return data.purchases;
    }
    return [];
  }

  /**
   * Re-bootstrap can re-fire this concurrently (see #3713) once the caller stopped awaiting it
   * (#3712). `@sequentialize` shares one in-flight promise so a second concurrent call can't
   * re-read the same unstamped watermark and double the toast.
   */
  @sequentialize(() => "purchaseHistoryCheck")
  async checkAndReactToPurchaseHistory(): Promise<void> {
    // Capture before the history fetch starts so the watermark we stamp reflects the
    // pre-fetch instant, not end-of-run `now`.
    const checkedAt = new Date().toISOString();
    try {
      this.capturePurchaseIdFromReturnUrl();

      // An active Stripe return stores PURCHASE_ID_KEY before redirecting to Stripe.
      // Finalize here so the success toast is not lost when bootstrap runs before the credits
      // page loads or when the return URL omits stripe_session.
      if (localStorage.getItem(PURCHASE_ID_KEY)) {
        if (this.readFreshPendingPurchase()) {
          await this.finalizePendingStripeReturn();
          return;
        }
        this.clearPendingPurchaseKeys();
      }
      const since = localStorage.getItem(LAST_PURCHASE_CHECK_KEY) ?? undefined;
      const history = await this.getPurchaseHistory(since);

      let completedCount = 0;
      let failedCount = 0;
      for (const record of history) {
        if (record.status === "completed") {
          completedCount++;
        } else if (record.status === "failed") {
          failedCount++;
        }
      }

      if (completedCount > 0) {
        try {
          await this.fetch();
        } catch {
          this.log.warning("Failed to refresh balance after purchase history recovery");
        }
        this.notifyService.notify(NotificationEnum.success, "succeeded", "creditsPurchased");
      }
      if (failedCount > 0) {
        this.notifyService.notify(NotificationEnum.error, "errorOccurred", "purchaseFail");
      }
      // Stamp the watermark after reacting, using the timestamp captured before the
      // history fetch started (not end-of-run `now`) so purchases that complete while
      // this check is in flight aren't skipped by the next check's `since` cursor. Also
      // means a transient fetch() failure or app crash between the fetch and the toast
      // does not advance the cursor past purchases whose notifications were never delivered.
      localStorage.setItem(LAST_PURCHASE_CHECK_KEY, checkedAt);
    } catch (error) {
      this.log.warning(
        `Purchase history recovery skipped: ${error instanceof Error ? error.message : String(error)}`,
      );
    } finally {
      this.clearStripeReturnFromUrl();
    }
  }

  bumpLastPurchaseCheck(): void {
    localStorage.setItem(LAST_PURCHASE_CHECK_KEY, new Date().toISOString());
  }

  /** The purchase_id in the pre-hash query string Stripe redirects to (e.g. /?purchase_id=…#/views). */
  returnUrlPurchaseId(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get(STRIPE_RETURN_PURCHASE_ID_PARAM)?.trim() || null;
  }

  /** Store the return URL's purchase_id as the pending purchase. */
  capturePurchaseIdFromReturnUrl(): boolean {
    const purchaseId = this.returnUrlPurchaseId();
    if (!purchaseId) {
      return false;
    }
    // Id before timestamp: if the id write throws, an older stored id keeps its old timestamp and
    // still expires instead of looking freshly pending.
    localStorage.setItem(PURCHASE_ID_KEY, purchaseId);
    localStorage.setItem(PURCHASE_ID_TS_KEY, new Date().toISOString());
    return true;
  }

  /** Strip purchase_id from the browser URL after handling a Stripe return. */
  clearStripeReturnFromUrl(): void {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(STRIPE_RETURN_PURCHASE_ID_PARAM)) {
      return;
    }
    url.searchParams.delete(STRIPE_RETURN_PURCHASE_ID_PARAM);
    const cleanUrl = `${url.pathname}${url.search}${url.hash}`;
    history.replaceState({}, document.title, cleanUrl);
  }

  private readFreshPendingPurchase(): { purchaseId: string } | null {
    const purchaseId = localStorage.getItem(PURCHASE_ID_KEY);
    if (!purchaseId) {
      return null;
    }
    const ts = localStorage.getItem(PURCHASE_ID_TS_KEY);
    const tsMs = ts ? new Date(ts).getTime() : NaN;
    if (!ts || isNaN(tsMs) || Date.now() - tsMs > PURCHASE_ID_MAX_AGE_MS) {
      return null;
    }
    return { purchaseId };
  }

  private clearPendingPurchaseKeys(): void {
    localStorage.removeItem(PURCHASE_ID_KEY);
    localStorage.removeItem(PURCHASE_ID_TS_KEY);
    localStorage.removeItem(PURCHASE_ID_PACK_KEY);
  }

  /**
   * Adjust balance via deposit/withdrawal so total CrS across accounts matches targetAmount.
   * Uses auto: false so withdrawals do not trigger subscription auto-redeem.
   */
  async setTotalCreditsTo(targetAmount: number): Promise<number> {
    if (!Number.isFinite(targetAmount) || targetAmount < 0) {
      throw new Error("Target credits must be a non-negative number");
    }

    if (this.accounts().length === 0) {
      await this.fetch();
    }

    const account = this.accounts()[0];
    if (!account?._id) {
      throw new Error("Gloss Credits account is not available");
    }

    const current = this.calculateTotalCredits(this.accounts(), GLOSS_CREDITS_SYMBOL);
    const delta = Math.round((targetAmount - current) * 100) / 100;

    if (Math.abs(delta) < 0.01) {
      return current;
    }

    if (delta > 0) {
      await this.createTransaction(
        account._id,
        TransactionType.DEPOSIT,
        GLOSS_CREDITS_SYMBOL,
        delta,
        false,
      );
    } else {
      await this.createTransaction(
        account._id,
        TransactionType.WITHDRAWAL,
        GLOSS_CREDITS_SYMBOL,
        Math.abs(delta),
        false,
      );
    }

    await this.fetch();
    return this.totalGoldCredits();
  }

  /**
   * Waits until the credit account is hydrated before a referral deposit.
   *
   * Readiness == the account record exists (has an `_id`), NOT balance > 0:
   * a user who has spent all their credits has a valid, fully-initialised
   * account with balance 0 and must not be made to spin. Poll the account
   * until it exists, bounded so we never hang.
   */
  private async ensureCreditSystemReady(): Promise<boolean> {
    const maxAttempts = 6;
    const delayMs = 500;
    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await this.fetch();
      if (this.accounts()[0]?._id) {
        return true;
      }
      await new Promise<void>((resolve) => setTimeout(resolve, delayMs));
    }
    return !!this.accounts()[0]?._id;
  }

  async createTransactionOnTaskComplete(
    reward: number,
    currency:
      | typeof GLOSS_CREDITS_SYMBOL
      | typeof GLOSS_PLATINUM_CREDITS_SYMBOL = GLOSS_CREDITS_SYMBOL,
    source?: ClientTransactionSource,
  ): Promise<boolean> {
    try {
      await this.ensureCreditSystemReady();

      const account = this.accounts()[0];
      if (!account?._id) {
        return false;
      }

      await this.createTransaction(
        account._id,
        TransactionType.DEPOSIT,
        currency,
        reward,
        undefined,
        source,
      );
      return true;
    } catch (error) {
      const errorMsg = `Error creating transaction: ${error instanceof Error ? error.message : String(error)}`;
      this.log.error(errorMsg);
      return false;
    }
  }

  /** Throws a uniform `${label}: ${reason}` error when the response status is outside 2xx. */
  private assertOk<T>(response: { status: number; data: T }, label: string): T {
    if (response.status < 200 || response.status > 299) {
      const message = (response.data as { message?: string } | undefined)?.message;
      throw new Error(`${label}: ${message ?? `HTTP ${response.status}`}`);
    }
    return response.data;
  }

  private postCheckoutRequest<T>(endpoint: string, apiKey: string, productId: string) {
    return capFetch<T>({
      url: `${this.baseUrl}${endpoint}`,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${apiKey}`,
      },
      data: { productId },
    });
  }

  private async saveNewTransactionToVault(txData: ITransactionResponse): Promise<void> {
    try {
      const account = this.glossCreditsAccountService.findGlossCreditsAccount();
      if (!account || !txData?._id) {
        return;
      }

      const existingIds = new Set(
        (this.transactionStoreService.transaction.transactionViews() ?? [])
          .filter((t) => t.accountLink?.id === account.id && t.id?.startsWith("gloss-credits-"))
          .map((t) => t.id),
      );

      if (existingIds.has(`gloss-credits-${txData._id}`)) {
        return;
      }

      const storeModel = mapGlossCreditsTransactionToStoreModel(txData, account);
      await this.transactionStoreService.transaction.saveToVault(
        [new TransactionView(storeModel)],
        true,
      );
    } catch (error) {
      this.log.error(`Failed to save credits transaction to vault: ${error?.message || error}`);
    }
  }

  private async fetchUserAccounts(): Promise<ICreditAccount[]> {
    const username = this.userName();
    if (!username) {
      return [];
    }
    this.syncConfigFromVault();
    const token = this.config()?.apiKey ?? null;
    const url = `${this.baseUrl}${CREDIT_CONFIG.endpoint.userAccounts}?username=${encodeURIComponent(username)}`;
    const response = await capFetch<{ accounts?: ICreditAccount[] }>({
      url,
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
    if (response.status < 200 || response.status > 299) {
      this.log.error(
        `Credit user accounts request failed: ${response.status} ${JSON.stringify(response.data)}`,
      );
      return [];
    }
    return response.data?.accounts ?? [];
  }

  private async fetchCredits(): Promise<void> {
    try {
      const url = `${this.baseUrl}${CREDIT_CONFIG.endpoint.transactions}`;
      const res = await this.glossCreditsApi.query<{ transactions: ITransactionResponse[] }>(url);
      const list = res.transactions ?? [];
      this._transactions.set(list.map((transaction) => this.mapToView(transaction)));
    } catch (error) {
      this.log.error(`Error fetching credits: ${String(error)}`);
    }
  }

  private mapToView(data: ITransactionResponse): CreditTransaction {
    return {
      id: data._id,
      accountId: data.accountId,
      currency: data.currency,
      createdAt: data.createdAt,
      amount: data.amount,
      type: data.type,
      source: data.source,
      conversionId: data.conversionId,
      balanceAfter: data.balanceAfter,
    };
  }

  private calculateTotalCredits(accounts: ICreditAccount[], currency?: string): number {
    return accounts.reduce((sum, account) => {
      const accountTotal =
        account.balances?.reduce((balanceSum, b) => {
          if (currency && b.currency && b.currency !== currency) {
            return balanceSum;
          }
          return balanceSum + (b.amount || 0);
        }, 0) || 0;
      return sum + accountTotal;
    }, 0);
  }
}
